import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './components/UI.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import DatabaseService from './services/DatabaseService';
import { isSupabaseConfigured } from './lib/supabaseClient';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import Loading from './components/Loading';

// Performance monitoring
if (process.env.NODE_ENV === 'development') {
  import('./utils/PerformanceMonitor');
}

const lazyWithRetry = (importer) => React.lazy(async () => {
  try {
    return await importer();
  } catch (error) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    return importer();
  }
});

// Lazy load ALL heavy components for better code splitting
const Home = lazyWithRetry(() => import('./pages/Home'));
const Properties = lazyWithRetry(() => import('./pages/Properties'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Admin = lazyWithRetry(() => import('./pages/Admin'));
const PropertyDetail = lazyWithRetry(() => import('./pages/PropertyDetail'));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [properties, setProperties] = useState([]);

  // Initialize app and load data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        const existingProperties = await DatabaseService.getAllProperties();

        if (!isSupabaseConfigured && existingProperties.length === 0) {
          const sampleProperties = [
            {
              title: 'Modern Villa',
              location: 'Bebek / İstanbul',
              size: 350,
              rooms: 4,
              price: 12000000,
              description: 'Boğaz manzaralı modern villa',
              images: ['https://picsum.photos/seed/villa1/600/400']
            },
            {
              title: 'Deniz Manzaralı Daire',
              location: 'Kadıköy / İstanbul',
              size: 120,
              rooms: 3,
              price: 2500000,
              description: 'Deniz manzaralı ferah daire',
              images: ['https://picsum.photos/seed/apartment1/600/400']
            },
            {
              title: 'Şehir Merkezi Ofis',
              location: 'Şişli / İstanbul',
              size: 85,
              rooms: 2,
              price: 1800000,
              description: 'Merkezi konumda modern ofis',
              images: ['https://picsum.photos/seed/office1/600/400']
            },
            {
              title: 'Bahçeli Müstakil Ev',
              location: 'Etiler / İstanbul',
              size: 280,
              rooms: 5,
              price: 8500000,
              description: 'Geniş bahçeli müstakil ev',
              images: ['https://picsum.photos/seed/house1/600/400']
            },
            {
              title: 'Lüks Penthouse',
              location: 'Nişantaşı / İstanbul',
              size: 200,
              rooms: 4,
              price: 15000000,
              description: 'Şehrin en prestijli lokasyonunda penthouse',
              images: ['https://picsum.photos/seed/penthouse1/600/400']
            },
            {
              title: 'Yatırımlık Daire',
              location: 'Bakırköy / İstanbul',
              size: 95,
              rooms: 2,
              price: 1200000,
              description: 'Yatırım için ideal konum',
              images: ['https://picsum.photos/seed/investment1/600/400']
            }
          ];

          for (const property of sampleProperties) {
            await DatabaseService.addProperty(property);
          }
        }

        const allProperties = await DatabaseService.getProperties();

        if (!isSupabaseConfigured) {
          const updatedProperties = allProperties.map((property) => {
            if (!property.createdAt) {
              const randomDaysAgo = Math.floor(Math.random() * 30);
              const createdDate = new Date();
              createdDate.setDate(createdDate.getDate() - randomDaysAgo);

              return {
                ...property,
                createdAt: createdDate.toISOString(),
                updatedAt: new Date().toISOString()
              };
            }
            return property;
          });

          if (updatedProperties.some((prop, index) => prop.createdAt !== allProperties[index]?.createdAt)) {
            localStorage.setItem(
              'bi_gayrimenkul_properties',
              JSON.stringify(updatedProperties)
            );
          }

          setProperties(updatedProperties);
        } else {
          setProperties(allProperties);
        }

        const savedUser = await DatabaseService.getCurrentUser();
        if (savedUser) {
          setIsAuthenticated(true);
          setCurrentUser(savedUser);
        }
      } catch (error) {
        console.error('App başlatma hatası:', error);
      }
    };

    initializeApp();
  }, []);

  const handleLogin = (user) => {
    if (user) {
      setIsAuthenticated(true);
      setCurrentUser(user);
    }
  };

  const handleLogout = async () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    await DatabaseService.logout();
  };

  const handleAddProperty = async (newProperty) => {
    const addedProperty = await DatabaseService.addProperty(newProperty);
    const updatedList = await DatabaseService.getProperties();
    setProperties(updatedList);
    return addedProperty;
  };

  const handleUpdateProperty = async (propertyId, updatedProperty) => {
    const updated = await DatabaseService.updateProperty(propertyId, updatedProperty);
    if (updated) {
      const updatedList = await DatabaseService.getProperties();
      setProperties(updatedList);
    }
    return updated;
  };

  const handleDeleteProperty = async (propertyId) => {
    const deleted = await DatabaseService.deleteProperty(propertyId);
    if (deleted) {
      const updatedList = await DatabaseService.getProperties();
      setProperties(updatedList);
    }
    return deleted;
  };

  const handleImageUpload = (propertyId, files) => {
    return DatabaseService.uploadImages(propertyId, files);
  };

  const homePage = (
    <Suspense fallback={<Loading size="large" text="Sayfa yükleniyor..." />}>
      <Navbar />
      <Home />
      <Footer isAuthenticated={isAuthenticated} onLogout={handleLogout} />
    </Suspense>
  );

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <div className="site-wrapper">
            <Routes>
              <Route path="/" element={homePage} />
              <Route path="/hakkimizda" element={homePage} />
              <Route path="/iletisim" element={homePage} />

              <Route path="/properties" element={
                <Suspense fallback={<Loading size="large" text="İlanlar yükleniyor..." />}>
                  <Properties />
                </Suspense>
              } />

              <Route path="/ilan/:id" element={
                <Suspense fallback={<Loading size="large" text="İlan detayı yükleniyor..." />}>
                  <PropertyDetail />
                </Suspense>
              } />

              <Route path="/login" element={
                isAuthenticated ? <Navigate to="/admin" replace /> :
                  <Suspense fallback={<Loading size="medium" text="Giriş sayfası yükleniyor..." />}>
                    <Login onLogin={handleLogin} />
                  </Suspense>
              } />

              <Route path="/admin" element={
                isAuthenticated ? (
                  <Suspense fallback={<Loading size="large" text="Admin panel yükleniyor..." />}>
                    <Admin
                      properties={properties}
                      currentUser={currentUser}
                      onAddProperty={handleAddProperty}
                      onUpdateProperty={handleUpdateProperty}
                      onDeleteProperty={handleDeleteProperty}
                      onImageUpload={handleImageUpload}
                      onLogout={handleLogout}
                    />
                  </Suspense>
                ) : (
                  <Navigate to="/login" replace />
                )
              } />
            </Routes>
          </div>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
